/*
 * Copyright (c) 2020. American Express Company
 */

import _ from 'lodash';
import { LocalDate } from '@js-joda/core';
import React, { Component, ReactComponentElement } from 'react';

// models and services
import { CardModel } from 'src/types/card.model';
import { CashFlowModel } from 'src/types/cash-flow.model';
import { goalIcons } from 'src/bw/goal/Goal';
import CardService from '../../../services/card.service';
import LiteralsService from '../../../services/literals/LiteralsService';
import NavigationService from '../../../services/navigation.service';
import FormatService from '../../../services/format.service';
import UtilsService from '../../../services/utils.service';

// components
import BackButton from '../../../components/back-button/BackButton';
import BwCard from '../../../components/card/BwCard';
import { GlyphRight } from '@americanexpress/dls-react';
import Typography from '../../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// icons and scss
import './TimelineDrawer.scss';

export default class TimelineDrawer extends Component<any> {
    public override render(): ReactComponentElement<any> {
        if (!this.props.user) return null;

        let lastCard: CardModel = null;
        const thisYear: number = LocalDate.now().year();
        const rawCashFlow: CashFlowModel[] = this.props.user.rawCashFlow;
        const cashFlowDeltas = rawCashFlow.filter((cashFlow: CashFlowModel, index: number) => {
            return (
                index === 0 ||
                cashFlow.dangerZone !== rawCashFlow[index - 1].dangerZone ||
                cashFlow.danger !== rawCashFlow[index - 1].danger
            );
        });

        for (let i = 0; i < cashFlowDeltas.length - 1; i++) {
            cashFlowDeltas[i].endDate = cashFlowDeltas[i + 1].startDate.minusMonths(1);
        }

        const stuff: any[] = this.props.user.cards.concat(cashFlowDeltas);
        const sortedStuff: any[] = _.orderBy(stuff, ['startDate', 'description'], ['asc', 'desc']);
        let dangerLevel: number = 0;

        return (
            <div className="timelineDrawer drawer">
                <BackButton drawerProps={this.props.drawerProps} handleBack={this.props.handleBack} />
                <Typography variant={TypographyVariants.HEADING_M} className="title">
                    {LiteralsService.getLiteral('TimelineDrawer.title')}
                </Typography>
                {/* <ScenarioMenu
                    user={this.props.user}
                    handleReloadUser={this.props.handleReloadUser} /> */}
                <div className="cards">
                    {sortedStuff.map((cardOrCash: any, index: number) => {
                        const showYear: boolean =
                            lastCard === null || lastCard.startDate.year() !== cardOrCash.startDate.year();
                        lastCard = cardOrCash;

                        let card: CardModel = null;
                        let showRow: boolean = true;
                        if (cardOrCash.description) {
                            card = cardOrCash;
                        } else {
                            dangerLevel = cardOrCash.danger;
                            showRow = cardOrCash.dangerZone;
                        }

                        let cardIcon: JSX.Element = null;
                        if (card) {
                            const hasCustomImage: boolean = Boolean(card?.hash);
                            if (hasCustomImage) {
                                cardIcon = <img alt="{CardService.getName()}" src={CardService.getPhotoUrl(card)} />;
                            } else {
                                cardIcon = <div className="icon">{goalIcons[card.description]}</div>;
                            }
                        }

                        return showRow ? (
                            <React.Fragment>
                                {showYear ? (
                                    <div className="yearRow" key={`yearRow${index}`}>
                                        <div className={`dangerLevel danger${dangerLevel}`} />
                                        <Typography
                                            variant={TypographyVariants.SUBTEXT}
                                            className="year"
                                            key={`year ${index}`}
                                        >
                                            {cardOrCash.startDate.year()}
                                            {cardOrCash.startDate.year() === thisYear ? (
                                                <Typography variant={TypographyVariants.LEGAL_PRIMARY}>
                                                    &nbsp;{LiteralsService.getLiteral('TimelineDrawer.thisYear')}
                                                </Typography>
                                            ) : null}
                                        </Typography>
                                    </div>
                                ) : null}
                                <div className="cardRow" key={`cardRow${index}`}>
                                    <div className={`dangerLevel danger${dangerLevel}`} />
                                    {card ? (
                                        <BwCard
                                            key={`card${index}`}
                                            className="goal"
                                            fullWidth={true}
                                            onClick={async () =>
                                                await NavigationService.goTo(
                                                    this.props.drawerProps.history,
                                                    `/card/${card.id}`
                                                )
                                            }
                                        >
                                            <div className="icon-n-text">
                                                {cardIcon}
                                                <div className="card-right">
                                                    <Typography
                                                        variant={TypographyVariants.HEADING_XS}
                                                        className="goals-name"
                                                    >
                                                        {card.nickname
                                                            ? card.nickname
                                                            : UtilsService.capitalizeFirstLetter(card.description)}
                                                    </Typography>
                                                    <Typography
                                                        variant={TypographyVariants.CAPTION}
                                                        className="goals-date"
                                                    >
                                                        {this.formatDates(cardOrCash)}
                                                    </Typography>
                                                </div>
                                            </div>
                                            <GlyphRight className="clickIcon" />
                                        </BwCard>
                                    ) : (
                                        <BwCard key={`cashflow${index}`} className="danger" fullWidth={true}>
                                            <div className={`warningIcon danger${dangerLevel}`} />
                                            <div className="card-right">
                                                <Typography
                                                    variant={TypographyVariants.HEADING_XS}
                                                    className="danger-name"
                                                >
                                                    {LiteralsService.getLiteral(
                                                        `TimelineDrawer.dangerLevel${dangerLevel}`
                                                    )}
                                                </Typography>
                                                <Typography
                                                    variant={TypographyVariants.CAPTION}
                                                    className="danger-date"
                                                >
                                                    {this.formatDates(cardOrCash)}
                                                </Typography>
                                            </div>
                                        </BwCard>
                                    )}
                                </div>
                            </React.Fragment>
                        ) : null;
                    })}
                </div>
            </div>
        );
    }

    private formatDates(cardOrCash: any): string {
        if (cardOrCash.startDate.equals(cardOrCash.endDate)) {
            return FormatService.formatDateMonth(cardOrCash.startDate);
        } else {
            return `${FormatService.formatDateMonth(cardOrCash.startDate)} - ${FormatService.formatDateMonth(
                cardOrCash.endDate
            )}`;
        }
    }
}
